import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from '../../utils/datetime';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BoltIcon from '@mui/icons-material/Bolt';
import HistoryIcon from '@mui/icons-material/History';
import InsightsIcon from '@mui/icons-material/Insights';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';

import { fetchRequest as goalsFetch } from '../../redux/actions/discovery/goals';
import {
  fetchRequest as forecastFetch,
  clearState as forecastClear,
  rerunRequest as forecastRerun,
} from '../../redux/actions/discovery/studioForecast';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';
import { FanChart, ScoreBars, ForecastValuesTable } from '../common/ForecastCharts';

const GOAL_TYPE_LABELS = {
  forecast_value: 'Forecast value',
  forecast_distribution: 'Forecast distribution',
  trend: 'Trend',
  anomaly: 'Anomaly detection',
};

// Classification goals (anomaly / trend) emit a probability in [0,1], not a
// price/level — they must be rendered as a likelihood, never plotted on the
// target's price axis (which makes a calm "0%" look like a crash to zero).
const SCORE_SERVING_MODES = new Set(['anomaly_score', 'trend_direction']);
const isScoreForecast = (data) => {
  const sm = data?.forecast?.serving_mode;
  if (sm) return SCORE_SERVING_MODES.has(sm);
  return data?.goal_type === 'anomaly' || data?.goal_type === 'trend';
};
const isAnomalyMode = (data) => {
  const sm = data?.forecast?.serving_mode;
  if (sm) return sm === 'anomaly_score';
  return data?.goal_type === 'anomaly';
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  // Include the year — the horizon end routinely lands in a later year, so an
  // unqualified "Jul 19" is ambiguous.
  try { return format(new Date(iso), 'MMM d, yyyy, HH:mm'); } catch (_e) { return '—'; }
};

// Compact relative age ("3h ago") for the forecast's served time. Full
// timestamp lives in the tooltip; this keeps the top-right readout glanceable.
const relTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const fmtNum = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const a = Math.abs(v);
  // Thousands separators + magnitude-aware precision (64114 -> "64,114").
  const maxDigits = a >= 1000 ? 0 : a >= 10 ? 2 : 4;
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  });
};

/* FanChart / ScoreBars / ForecastValuesTable now live in
   ../common/ForecastCharts so the Forecast Studio and Inference detail page
   share one identical, interactive (hover + zoom) chart implementation. */

/* ------------------------------------------------------------------ */
/* Intent-specific headline                                            */
/* ------------------------------------------------------------------ */

// Honest-confidence guard: the champion's held-out skill — the forecast skill
// score vs a naive PERSISTENCE reference for regression (does it beat carrying
// the last value forward?), or AUC-derived for classifiers. When it's <= 0 the
// model adds nothing over a coin flip / carry-forward, so a confident-looking
// headline ("Strong upward signal 100%") is misleading — surface the caveat.
const noSkill = (data) => {
  const s = data?.forecast?.model_quality?.skill_score;
  return s != null && Number(s) <= 0;
};

const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

// A single labelled stat pill (value + qualitative note + explanatory tip).
const QualityPill = ({ label, value, tip, color = 'text.primary', note }) => {
  const pill = (
    <Stack
      direction="row" spacing={0.75} alignItems="baseline"
      sx={{
        px: 1.5, py: 0.6, borderRadius: '999px',
        border: '1px solid', borderColor: 'divider',
        bgcolor: 'action.hover', whiteSpace: 'nowrap',
      }}
    >
      <Typography variant="subtitle2" sx={{ color, fontWeight: 700, lineHeight: 1 }}>
        {value}
      </Typography>
      {note && (
        <Typography variant="caption" sx={{ color }}>{note}</Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
  return tip ? <Tooltip arrow title={tip}>{pill}</Tooltip> : pill;
};

// Always-on model-quality readout: how well the champion scored on held-out
// data (accuracy + skill), adapting to regression vs classification.
const ModelQualityBar = ({ data }) => {
  const mq = data?.forecast?.model_quality;
  if (!mq) return null;
  const acc = mq.accuracy_score;
  const skill = mq.skill_score;
  if (acc == null && skill == null) return null;
  const isClass = mq.score_kind === 'classification';

  const skillColor = skill == null ? 'text.secondary'
    : skill <= 0 ? 'text.disabled'
      : skill < 15 ? 'warning.main'
        : skill < 40 ? 'info.main'
          : 'success.main';
  const skillNote = skill == null ? '—'
    : skill <= 0 ? 'no skill'
      : skill < 15 ? 'modest'
        : skill < 40 ? 'solid'
          : 'strong';

  const accLabel = isClass ? 'Class separation' : 'Accuracy';
  const skillLabel = isClass ? 'Skill over chance' : 'Skill vs persistence';
  const skillTip = isClass
    ? 'Value-add over a coin-flip / base-rate guess. 0% = no better than chance, 100% = perfect separation.'
    : 'Held-out back-test of the DEPLOYED model at the goal\u2019s horizon: how much it beats carrying the forecast-origin value forward. Measured on the exact artifact serving this forecast \u2014 it can differ from the leaderboard\u2019s walk-forward skill (a separate fit of the same recipe). 0% = no better than persistence.';

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
      alignItems="center" justifyContent="flex-end"
      sx={{ flexShrink: 0 }}
    >
      <AccuracyInfoTooltip
        value={typeof acc === 'number' ? acc : null}
        scoreKind={isClass ? 'classification' : undefined}
        note="Back-test of the deployed model at the goal's horizon — can differ from the leaderboard's walk-forward accuracy."
      >
        <Box sx={{ display: 'inline-flex' }}>
          <QualityPill label={accLabel} value={fmtPct(acc)} />
        </Box>
      </AccuracyInfoTooltip>
      {!isClass && acc != null && (
        <QualityPill
          label="WAPE"
          value={fmtPct(Math.max(0, 100 - Number(acc)))}
          tip="Weighted Absolute Percentage Error on held-out data: total absolute error ÷ total actuals. Lower is better. Accuracy above is 100% − WAPE."
        />
      )}
      <QualityPill
        label={skillLabel} value={fmtPct(skill)} tip={skillTip}
        color={skillColor} note={skillNote}
      />
    </Stack>
  );
};

const IntentHeadline = ({ data }) => {
  const fc = data?.forecast;
  if (!fc?.point?.length) return null;
  const flat = noSkill(data);

  // Classification goals (anomaly / trend): the point path IS a probability.
  // Render it as a likelihood, not a price delta.
  if (isScoreForecast(data)) {
    const anomaly = isAnomalyMode(data);
    const pts = fc.point.map((v) => Math.max(0, Math.min(1, Number(v) || 0)));
    const peak = pts.length ? Math.max(...pts) : null;
    const pct = peak == null ? null : peak * 100;
    let color = 'text.secondary';
    let label = '—';
    if (pct != null) {
      if (pct >= 70) {
        color = 'error.main';
        label = anomaly ? 'High anomaly risk' : 'Strong upward signal';
      } else if (pct >= 40) {
        color = 'warning.main';
        label = anomaly ? 'Elevated' : 'Mild upward signal';
      } else {
        color = 'success.main';
        label = anomaly ? 'Normal — no anomaly detected' : 'No upward signal';
      }
    }
    // A no-skill model must not present as a confident call: neutralize the
    // color and replace the "strong signal" label with an explicit caveat.
    if (flat) {
      color = 'text.secondary';
      label = 'low confidence · no skill';
    }
    const Icon = anomaly ? InsightsIcon : TrendingUpIcon;
    return (
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Icon sx={{ fontSize: 34, color }} />
          <Box>
            <Typography variant="h6" sx={{ color, lineHeight: 1.2 }}>
              {anomaly ? 'Anomaly likelihood' : 'Upward-trend likelihood'}
              {' '}{pct == null ? '—' : `${pct.toFixed(1)}%`}
              {pts.length > 1 ? ' · peak over horizon' : ''}
              <Typography component="span" variant="body2" sx={{ ml: 1, color }}>
                {label}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {anomaly
                ? 'Probability the model flags an anomaly over the horizon — low is normal. Drivers below are the validated precursors to watch.'
                : 'Probability the series moves upward over the horizon.'}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  const anchor = (() => {
    const vals = (fc.history?.values || []).filter((v) => v != null);
    return vals.length ? vals[vals.length - 1] : null;
  })();
  const last = fc.point[fc.point.length - 1];
  const delta = anchor != null ? last - anchor : null;
  const deltaPct = anchor ? (100 * delta) / Math.abs(anchor) : null;
  const up = delta != null && delta > 0;

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {delta != null && (up
          ? <TrendingUpIcon sx={{ fontSize: 32, color: flat ? 'text.secondary' : 'success.main' }} />
          : <TrendingDownIcon sx={{ fontSize: 32, color: flat ? 'text.secondary' : 'error.main' }} />)}
        <Box>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {fmtNum(last)}
            {deltaPct != null && (
              <Typography component="span" variant="body2" sx={{ ml: 1, color: flat ? 'text.secondary' : (up ? 'success.main' : 'error.main') }}>
                {up ? '+' : ''}{deltaPct.toFixed(1)}% vs now
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            at horizon end · {fmtTs(fc.step_at?.[fc.step_at.length - 1])}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Track record card                                                   */
/* ------------------------------------------------------------------ */

const TrackRecordCard = ({ record }) => {
  if (!record) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Track record</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Builds up as the best model serves: each forecast is scored against what
            actually happened, nightly. Check back after the first day of serving.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const headline = record.metric === 'hit_rate'
    ? (record.hit_rate != null ? `${(record.hit_rate * 100).toFixed(0)}%` : '—')
    : fmtNum(record[record.metric]);
  const headlineLabel = {
    rmse: 'RMSE', mae: 'MAE', hit_rate: 'direction hit rate',
  }[record.metric] || record.metric;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2">Track record</Typography>
          <Tooltip arrow title="Every served forecast scored against what actually happened — aggregated nightly. Same metric currency as the model fitting leaderboard.">
            <Chip size="small" variant="outlined" label={`${record.n_forecasts} forecasts scored`} />
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6">{headline}</Typography>
            <Typography variant="caption" color="text.secondary">{headlineLabel}</Typography>
          </Box>
          {record.coverage != null && (
            <Box>
              <Typography variant="h6">{(record.coverage * 100).toFixed(0)}%</Typography>
              <Typography variant="caption" color="text.secondary">
                of actuals fell inside the band ({record.coverage_n} steps)
              </Typography>
            </Box>
          )}
          {record.metric !== 'hit_rate' && record.hit_rate != null && (
            <Box>
              <Typography variant="h6">{(record.hit_rate * 100).toFixed(0)}%</Typography>
              <Typography variant="caption" color="text.secondary">direction hit rate</Typography>
            </Box>
          )}
        </Stack>
        {(record.points || []).length > 1 && (
          <PredictedVsRealized points={record.points} />
        )}
        <Typography variant="caption" color="text.disabled">
          Updated {fmtTs(record.updated_at)} · honesty as a feature: this is the model
          versus reality, not versus a backtest.
        </Typography>
      </CardContent>
    </Card>
  );
};

const PredictedVsRealized = ({ points, height = 120 }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(560);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const all = points.flatMap((p) => [p.predicted, p.realized]).filter((v) => v != null);
  if (!all.length) return null;
  let yMin = Math.min(...all);
  let yMax = Math.max(...all);
  if (yMin === yMax) { yMin -= 1; yMax += 1; }
  const M = { top: 6, right: 6, bottom: 6, left: 6 };
  const W = Math.max(width, 240);
  const x = (i) => M.left + ((W - M.left - M.right) * i) / Math.max(points.length - 1, 1);
  const y = (v) => M.top + (height - M.top - M.bottom) * (1 - (v - yMin) / (yMax - yMin));
  const path = (key) => points
    .map((p, i) => (p[key] == null ? null : `${i === 0 || points[i - 1]?.[key] == null ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`))
    .filter(Boolean).join(' ');

  return (
    <Box ref={containerRef} sx={{ width: '100%', mb: 0.5 }}>
      <svg width={W} height={height} style={{ display: 'block' }}>
        <path d={path('realized')} fill="none" stroke="currentColor" strokeWidth={1.5} opacity={0.65} />
        <path d={path('predicted')} fill="none" stroke="#614dee" strokeWidth={1.5} strokeDasharray="5 3" />
      </svg>
      <Stack direction="row" spacing={2} sx={{ ml: 0.5 }}>
        <Typography variant="caption" color="text.secondary">— realized</Typography>
        <Typography variant="caption" sx={{ color: '#614dee' }}>┄ predicted (1 step ahead)</Typography>
      </Stack>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const ForecastStudioPage = ({ embedded = false, goalId: goalIdProp, hideTypeChip = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const goals = useSelector((s) => s.discovery_goals?.response) || [];
  const goalsLoading = useSelector((s) => Boolean(s.discovery_goals?.isLoading));
  const fcState = useSelector((s) => s.discovery_studio_forecast);
  const rerunLoading = Boolean(fcState?.rerunLoading);

  const servable = useMemo(
    () => goals.filter((g) => g.champion_pipeline_id),
    [goals],
  );
  const urlGoal = searchParams.get('goal') || '';
  const goalId = embedded
    ? (goalIdProp || '')
    : (urlGoal && goals.some((g) => g.goal_id === urlGoal)
      ? urlGoal
      : (servable[0]?.goal_id || ''));

  useEffect(() => { dispatch(goalsFetch()); }, [dispatch]);

  useEffect(() => {
    if (!goalId) return undefined;
    dispatch(forecastFetch({ goalId }));
    return () => {
      dispatch(forecastClear());
    };
  }, [dispatch, goalId]);

  const data = fcState?.response;
  const inShock = data?.shock_state === 'shock' || data?.shock_state === 'recovery';

  // Cold-start handling: an idle model server takes a while to come up, so
  // the first load can be slow (the backend waits for it) or come back with
  // no forecast yet. Neither is an error — surface a "warming up" state and
  // keep polling until the first forecast lands.
  const WARM_POLL_MS = 15000;
  const WARM_POLL_MAX = 8; // ~2 more minutes of quiet polling

  const [slowLoad, setSlowLoad] = useState(false);
  useEffect(() => {
    if (!fcState?.isLoading) { setSlowLoad(false); return undefined; }
    const t = setTimeout(() => setSlowLoad(true), 6000);
    return () => clearTimeout(t);
  }, [fcState?.isLoading]);

  const warmAttempts = useRef(0);
  useEffect(() => { warmAttempts.current = 0; }, [goalId]);
  const waitingOnModel = Boolean(
    data && !data.forecast && data.champion_pipeline_id
    && warmAttempts.current < WARM_POLL_MAX,
  );
  useEffect(() => {
    if (!goalId || !waitingOnModel || fcState?.isLoading) return undefined;
    const t = setTimeout(() => {
      warmAttempts.current += 1;
      dispatch(forecastFetch({ goalId }));
    }, WARM_POLL_MS);
    return () => clearTimeout(t);
  }, [dispatch, goalId, waitingOnModel, fcState?.isLoading, data]);

  return (
    <Box sx={{ width: '100%' }}>
      {!embedded && (
        <>
          <PageBreadcrumbs items={[{ text: 'Forecast Studio', icon: <QueryStatsIcon /> }]} mb={0.5} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
            Each goal&apos;s best model serving live — multi-horizon forecasts with
            calibrated bands, an honest track record against reality, and the validated
            drivers behind every call.
          </Typography>
        </>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {!embedded && (
          <TextField
            select size="small" label="Goal" value={goalId || ''}
            onChange={(e) => setSearchParams({ goal: e.target.value })}
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
            disabled={goalsLoading || servable.length === 0}
          >
            {servable.map((g) => (
              <MenuItem key={g.goal_id} value={g.goal_id}>
                {g.name || g.target_key}
                {' '}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  · {GOAL_TYPE_LABELS[g.goal_type] || g.goal_type}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
        )}
        {data?.goal_type && !hideTypeChip && (
          <Chip size="small" variant="outlined" label={GOAL_TYPE_LABELS[data.goal_type] || data.goal_type} />
        )}
        {hideTypeChip && (
          <Chip size="small" variant="outlined" label="Fleet average" />
        )}
        {inShock && (
          <Tooltip
            arrow
            title={(() => {
              const trigger = data.shock_stream ? ` (trigger: ${data.shock_stream})` : '';
              const b = data.shock_blend;
              // A fitted blend means the platform LEARNED these weights from
              // realized shock outcomes — say so, with the actual mix.
              const mix = b && Number.isFinite(Number(b.champion))
                ? ` Blend fitted from past shocks: champion ${Math.round(Number(b.champion) * 100)}% · zero-shot ${Math.round(Number(b.zero_shot) * 100)}% · event overlay ${Math.round(Number(b.overlay) * 100)}%.`
                : ' Intervals widen and the event overlay blends in until recovery.';
              return `Shock-mode serving is active${trigger}.${mix}`;
            })()}
          >
            <Chip size="small" color="warning" icon={<BoltIcon />} label={data.shock_state} />
          </Tooltip>
        )}
        {data?.forecast && (
          <Tooltip arrow title="Re-run the deployed model on the latest data and refresh this forecast.">
            <span style={{ marginLeft: 'auto' }}>
              <CMButtonOutlined
                size="small"
                disabled={rerunLoading}
                sx={{ height: 32, fontSize: 12 }}
                startIcon={rerunLoading
                  ? <CircularProgress size={13} color="inherit" />
                  : <RefreshIcon sx={{ fontSize: 16 }} />}
                onClick={() => goalId && dispatch(forecastRerun({ goalId }))}
              >
                {rerunLoading ? 'Re-running…' : 'Re-run'}
              </CMButtonOutlined>
            </span>
          </Tooltip>
        )}
      </Stack>

      {goalsLoading && <CircularProgress size={24} />}

      {!embedded && !goalsLoading && servable.length === 0 && (
        <Alert severity="info" sx={{ maxWidth: 720 }}>
          No goal has a deployed model yet. Run discovery on a goal and let auto-fit
          deploy the best model — it appears here automatically, serving live.
          {goals.length === 0 && (
            <Box sx={{ mt: 1 }}>
              <CMButton size="small" onClick={() => navigate('/goals/create')}>Create a goal</CMButton>
            </Box>
          )}
        </Alert>
      )}

      {goalId && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              {fcState?.isLoading && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  {slowLoad && (
                    <Typography variant="body2" color="text.secondary">
                      The model is starting up — the first forecast can take a
                      minute or two…
                    </Typography>
                  )}
                </Stack>
              )}
              {!fcState?.isLoading && waitingOnModel && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    The model is warming up — waiting for its first forecast.
                    This page will update automatically.
                  </Typography>
                </Stack>
              )}
              {!fcState?.isLoading && !waitingOnModel && data && !data.forecast && (
                <Stack spacing={1.5} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary">
                    {data.champion_pipeline_id
                      ? 'The model hasn\u2019t served a forecast yet — it may still be starting up.'
                      : 'The best model hasn\u2019t served a forecast yet. Forecasts are logged the first time the deployed model is called — via live monitoring or the deployment\u2019s inference endpoint.'}
                  </Typography>
                  {data.champion_pipeline_id && (
                    <CMButtonOutlined
                      size="small"
                      disabled={rerunLoading}
                      sx={{ height: 32, fontSize: 12 }}
                      startIcon={rerunLoading
                        ? <CircularProgress size={13} color="inherit" />
                        : <RefreshIcon sx={{ fontSize: 16 }} />}
                      onClick={() => goalId && dispatch(forecastRerun({ goalId }))}
                    >
                      {rerunLoading ? 'Running…' : 'Run forecast now'}
                    </CMButtonOutlined>
                  )}
                </Stack>
              )}
              {data?.forecast && (
                <Stack spacing={1.5}>
                  <Stack
                    direction="row" spacing={2}
                    justifyContent="space-between" alignItems="flex-start"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <IntentHeadline data={data} />
                    </Box>
                    <Stack spacing={0.6} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                      <ModelQualityBar data={data} />
                      {data.forecast.at && (
                        <Tooltip arrow title={`Forecast generated ${fmtTs(data.forecast.at)}`}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              Generated {relTime(data.forecast.at) || fmtTs(data.forecast.at)}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                  {isScoreForecast(data) ? (
                    <ScoreBars forecast={data.forecast} anomaly={isAnomalyMode(data)} />
                  ) : (
                    <FanChart forecast={data.forecast} events={data.events} />
                  )}
                  <ForecastValuesTable
                    forecast={data.forecast}
                    mode={isScoreForecast(data) ? 'score' : 'fan'}
                    anomaly={isAnomalyMode(data)}
                  />
                  <Typography variant="caption" color="text.disabled">
                    Served {fmtTs(data.forecast.at)} ·
                    {' '}{data.forecast.horizon_bins} steps of
                    {' '}{Math.round((data.forecast.interval_seconds || 3600) / 60)}m ahead
                    {' '}· model {data.champion_pipeline_id?.slice(0, 8)}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
          <TrackRecordCard record={data?.track_record} />
        </Stack>
      )}
    </Box>
  );
};

export default ForecastStudioPage;
